// @file Landside navigation store
import { trackPageview } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import { getPathnameItems } from '@@/bits/location'
import { PricingPlansExperimentVariant } from '@@/enums'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import type { User } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum LandsidePage {
  Home = 'home',
  Product = 'product',
  Subscriptions = 'subscriptions',
  Templates = 'templates',
  Help = 'help',
  Careers = 'careers',
  WhatsNew = 'whatsnew',
  Pricing = 'pricing',
  DynamicLandingPage = 'dynamic_landing_page',
}

export enum ProductPageTab {
  HOW_IT_WORKS = 'howitworks',
  EDUCATION = 'education',
}

// Referenced from app/controllers/landside_controller.rb
const landsidePageTitles = {
  [LandsidePage.Home]: __('Padlet: Beauty will save the work'),
  [LandsidePage.Product]: __('Product - Padlet'),
  [LandsidePage.Templates]: __('Templates - Padlet'),
  [LandsidePage.Subscriptions]: __('Subscriptions - Padlet'),
  [LandsidePage.Pricing]: __('Pricing - Padlet'),
  [LandsidePage.DynamicLandingPage]: __('Padlet: Beauty will save the work'),
}

const productPageTabTitles = {
  [ProductPageTab.EDUCATION]: __('For education - Padlet'),
  [ProductPageTab.HOW_IT_WORKS]: __('How it works - Padlet'),
}

export const useLandsideNavigationStore = defineStore('landsideNavigation', () => {
  // State

  const currentPage = ref(LandsidePage.Home)
  const isUserLoggedIn = ref(false)
  const currentUser = ref<User | null>(null)
  const productPageTab = ref<ProductPageTab>(ProductPageTab.HOW_IT_WORKS)
  const pricingPlansExperimentVariant = ref<PricingPlansExperimentVariant>(PricingPlansExperimentVariant.Control)

  const excludedButtonTitle = ref<string | null>(null)

  // Getters
  const landsidePages = computed(() => [
    {
      key: LandsidePage.Home,
      text: __('Home'),
      href: '/',
      hrefMode: isUserLoggedIn.value ? OzBaseButtonHrefMode.Default : OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: LandsidePage.Product,
      text: __('Product'),
      href: '/site/product',
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: LandsidePage.Subscriptions,
      text: excludedButtonTitle.value === LandsidePage.Subscriptions ? __('Pricing') : __('Subscriptions'),
      href: '/site/subscriptions',
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: LandsidePage.Templates,
      text: __('Templates'),
      href: '/site/templates',
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: LandsidePage.Help,
      text: __('Help'),
      href: 'https://padlet.help',
      hrefMode: OzBaseButtonHrefMode.NewTab,
    },
    {
      key: LandsidePage.Careers,
      text: __('Careers'),
      href: 'https://padlet.jobs',
      hrefMode: OzBaseButtonHrefMode.NewTab,
    },
    // {
    //   key: LandsidePage.WhatsNew,
    //   text: __("What's new"),
    //   href: 'https://padlet.blog',
    //   hrefMode: OzBaseButtonHrefMode.NewTab,
    // },
  ])

  function initializeState(data: {
    currentPage: LandsidePage
    isUserLoggedIn: boolean
    currentUser: User
    pricingPlansExperimentVariant: PricingPlansExperimentVariant
  }): void {
    currentPage.value = data.currentPage ?? currentPage.value
    isUserLoggedIn.value = data.isUserLoggedIn ?? isUserLoggedIn.value
    currentUser.value = data.currentUser ?? currentUser.value
    pricingPlansExperimentVariant.value = data.pricingPlansExperimentVariant ?? pricingPlansExperimentVariant.value

    const excludedButtonTitleInStorage = localStorage.getItem('excludedButtonTitle')
    if (excludedButtonTitleInStorage === null) {
      const chosenPricingText = Math.random() < 0.5 ? LandsidePage.Pricing : LandsidePage.Subscriptions
      excludedButtonTitle.value = chosenPricingText
      localStorage.setItem('excludedButtonTitle', chosenPricingText)
    } else {
      excludedButtonTitle.value = excludedButtonTitleInStorage
    }
  }

  function setCurrentPage(page: LandsidePage): void {
    // If user is logged in, they should be directed to /dashboard and we want to prevent the UI from changing
    if (page === LandsidePage.Home && isUserLoggedIn.value) return
    if (page === LandsidePage.Help) return // Prevent the UI from changing since Help is an external url
    if (page === LandsidePage.Careers) return // Prevent the UI from changing since Careers is an external url
    if (page === LandsidePage.WhatsNew) return // Prevent the UI from changing since WhatsNew is an external url

    const userFocusedElement = document.activeElement as HTMLElement
    const pricingSectionElement = document.querySelector('[data-testid="landside-subscription-pricing-section"')

    currentPage.value = page
    trackPageview('Homepage', page.charAt(0).toUpperCase() + page.slice(1))

    // if user tries to navigate to subscriptions page, it should only reload if user is clicking at the footer
    if (page === LandsidePage.Subscriptions && pricingSectionElement?.contains(userFocusedElement) === false) {
      location.reload()
    }
  }

  function scrollToTop(): void {
    window.scrollTo({
      top: 0,
    })
  }

  function setCurrentPageWithSideEffects(page: LandsidePage): void {
    setCurrentPage(page)

    // Side effects
    scrollToTop()

    // Update document title
    document.title = landsidePageTitles[page]
  }

  function setProductPageTab(tab: ProductPageTab): void {
    productPageTab.value = Object.values(ProductPageTab).includes(tab) ? tab : ProductPageTab.HOW_IT_WORKS
  }

  function setProductPageTabWithSideEffects(tab: ProductPageTab): void {
    setProductPageTab(tab)

    // Side effects
    scrollToTop()

    // Update document title
    document.title = productPageTabTitles[tab]
  }

  function switchPageToUrlParams(): void {
    // The first element in getPathnameItems for Landside returns 'site'
    const pathNameItems = getPathnameItems().filter((pathNameItem) => pathNameItem !== 'site')
    let landsidePage: LandsidePage
    // If path represents a dynamic landing page, we set the landsidePage to DynamicLandingPage
    if (pathNameItems[0] === 'product' && pathNameItems[1] === 'features') {
      landsidePage = LandsidePage.DynamicLandingPage
    } else {
      landsidePage = pathNameItems[0] as LandsidePage
    }

    if (Object.values(LandsidePage).includes(landsidePage)) {
      setCurrentPageWithSideEffects(landsidePage)

      if (landsidePage === LandsidePage.Product) {
        const tab = pathNameItems[1] as ProductPageTab
        setProductPageTab(tab)
        return
      }

      return
    }

    // Fallthrough when landsidePage is not a valid landside page,
    // we set the homepage as the current page
    setCurrentPageWithSideEffects(LandsidePage.Home)
  }

  return {
    // State
    currentPage,
    productPageTab,
    isUserLoggedIn,
    currentUser,
    pricingPlansExperimentVariant,

    // Getters
    landsidePages,

    // Actions
    initializeState,
    scrollToTop,
    setCurrentPage,
    setCurrentPageWithSideEffects,
    setProductPageTab,
    setProductPageTabWithSideEffects,
    switchPageToUrlParams,
  }
})
